import React, { useCallback, useEffect, useState } from 'react';
import {
  withRouter,
} from 'react-router-dom';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import { Box, CircularProgress } from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import {
  FormContainer,
  StyledTextField,
} from './FormStyles';
import { useUser } from '../../contexts/UserContext';
import { emailRegex } from '../../utils/Regex';
import { getDetails, updateProfile } from '../../api/api';
import NotificationToast from '../notifications/NotificationToast';
import i18n from '../../i18n/i18n';
import { useTheme } from '../../contexts/ThemeContext';
import TaxCountryModal from '../modals/TaxCountryModal';
import { countryData } from '../../constants/CountryData';
import { getVatNumberError } from '../../utils/vatValidation';
import { TaxId } from '../../types/types';
import GalpUserMenu from '../GalpUserMenu';

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
`;

const StyledButton = styled(({ ...rest }) => (
  <Button
    {...rest}
  />
))`
  && {
    background-color: ${(props) => props.color};
    color: ${(props) => props.textColor};
    border-width: 0;
    border-radius: 10px;
    padding: 10px 20px;
    margin: 10px;
    width: 120px;
    text-transform: capitalize;
    font-size: 0.85em;

    ${({ disabled }) => disabled && `
     background-color: rgb(243, 243, 243);
     color: rgba(255,255,255,0.15)
     border-width: 0;
     border-radius: 10px;
     padding: 10px 20px;
     margin: 10px;
     width: 120px;
     text-transform: capitalize;
     font-size: 0.85em;
  `
}
`;

const StyledDiv = styled.div`
    padding-top: 20px;
    padding-bottom: 20px;
`;

const Spacer = styled.div`
  margin-bottom: 60px;
`;

type Error = {
  email: string | null,
  taxId: string | null,
}

const StyledSpan = styled.span`
    color: #d01818;
`;

const HelexiaInputWrapper = styled.div`
  background: #efefef;
  border-radius: 12px;
  height: 36px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
`;

const HelexiaInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  color: #3f3f3f;
  font-family: Geologica, sans-serif;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: -0.98px;
  width: 100%;
  &::placeholder {
    color: #6a6a6a;
  }
  &:disabled {
    color: #9a9a9a;
    cursor: not-allowed;
  }
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0px 1000px #efefef inset;
    box-shadow: 0 0 0px 1000px #efefef inset;
    -webkit-text-fill-color: #3f3f3f;
  }
`;

const ProfileForm = ({ history }) => {
  const { user, setUser } = useUser();

  const [name, setName] = useState<string | undefined>('');
  const [email, setEmail] = useState<string | undefined>('');
  const [taxId, setTaxId] = useState<TaxId>({ prefix: countryData.PT?.prefix || null, number: null });
  const [mobilePhone, setMobilePhone] = useState<string | null>('');
  const [changed, setChanged] = useState<boolean>(false);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notification, setNotification] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isSelectingCountry, setSelectingCountry] = useState(false);
  const [country, setCountry] = useState('PT');

  const { theme, themeName } = useTheme();
  const isHelexia = themeName === 'helexia';
  const isGalp = themeName === 'galp';
  const CountryFlag = countryData[country]?.component;

  const galpInitials = (() => {
    const authUser = (user as any)?.user;
    const source = authUser?.name || (authUser?.email || '').split('@')[0];
    const parts = source.split(/[\s._-]+/).filter(Boolean);
    const raw = parts.length >= 2 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : (parts[0]?.[0] || '');
    return raw.toUpperCase();
  })();

  const [error, setError] = useState<Error>({
    email: null,
    taxId: null,
  });

  const handleCountrySelected = useCallback((selectedCountry) => {
    setCountry(selectedCountry);
    setTaxId({ prefix: countryData[selectedCountry]?.prefix, number: '' });
    setSelectingCountry(false);
  }, []);

  const onSubmit = async (e) => {
    try {
      e.preventDefault();
      const nifError = getVatNumberError(taxId.prefix, taxId.number);
      if (nifError) {
        setError((prevState) => ({ ...prevState, taxId: i18n.t(nifError) }));
        return;
      }
      const fullTaxId = taxId.prefix && taxId.number ? taxId.prefix + taxId.number : null;
      await updateProfile(user, name, fullTaxId, mobilePhone);
      setNotification({
        severity: 'success',
        title: i18n.t('success'),
        message: i18n.t('profile_updated'),
      });
      setShowNotification(true);
    } catch (err) {
      setNotification({
        severity: 'error',
        title: i18n.t('error'),
        message: i18n.t('error_occured_try_later'),
      });
      setShowNotification(true);
    }
  };

  const getCustomerInfo = async () => {
    if (user) {
      try {
        const info = await getDetails(user);
        setUser({
          ...user,
          user: {
            name: info.name,
            email: info.email,
            tax: info.taxIdx,
            mobilePhone: info.mobilePhone,
          },
        });
        if (info.taxId) {
          const prefix = info.taxId.replace(/[\d]/g, '');
          const number = info.taxId.replace(/[^0-9]/g, '');
          setTaxId({ prefix, number });
          const countryEntry = Object.entries(countryData).find(([, data]) => data.prefix === prefix);
          if (countryEntry) {
            setCountry(countryEntry[0]);
          }
        }
        setName(info.name);
        setEmail(info.email);
        setMobilePhone(info.mobilePhone);
        setLoading(false);
      } catch (err) {
        setNotification({
          severity: 'error',
          title: i18n.t('error'),
          message: i18n.t('error_occured_try_later'),
        });
        setShowNotification(true);
      }
    }
  };

  useEffect(() => {
    getCustomerInfo();
  }, []);

  useEffect(() => {
    const nifError = getVatNumberError(taxId.prefix, taxId.number);
    setError((prevState) => ({
      ...prevState,
      email: email && !emailRegex.test(email) ? i18n.t('invalid_email') : null,
      taxId: nifError ? i18n.t(nifError) : null,
    }));
  }, [email, name, mobilePhone, taxId]);

  useEffect(() => {
    if (email !== user?.user?.email
        || name !== user?.user?.name
        || mobilePhone !== user?.user?.mobilePhone
        || (taxId.number && (taxId.prefix + taxId.number) !== user?.user?.taxId)
    ) {
      setChanged(true);
    } else {
      setChanged(false);
    }
  }, [mobilePhone, name, email, taxId, setChanged, user]);

  if (isGalp) {
    const galpFieldGroup: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      width: '100%',
    };
    const galpLabel: React.CSSProperties = {
      fontSize: '14px',
      lineHeight: '22px',
      color: '#111110',
      margin: 0,
    };
    const galpInputBox: React.CSSProperties = {
      border: '2px solid #bab9ba',
      borderRadius: '14px',
      height: '46px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '0 10px',
      boxSizing: 'border-box',
      width: '100%',
    };
    const galpInput: React.CSSProperties = {
      border: 'none',
      outline: 'none',
      background: 'transparent',
      width: '100%',
      fontFamily: theme.bodyFont,
      fontSize: '16px',
      color: '#0b0b0b',
    };
    const galpErrorStyle: React.CSSProperties = { color: '#d01818', fontSize: '12px', margin: '4px 0 0 0' };
    const submitDisabled = !changed || !!error.email || !!error.taxId;

    return (
      <div style={{
        width: '100%',
        height: '100dvh',
        background: 'white',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        fontFamily: theme.bodyFont,
      }}
      >
        <NotificationToast
          open={showNotification}
          message={notification && notification.message}
          title={notification && notification.title}
          severity={notification && notification.severity}
          onClose={() => setShowNotification(false)}
          bottomOffset={96}
          sideMargin={16}
        />
        <TaxCountryModal
          isSelectingCountry={isSelectingCountry}
          setSelectingCountry={setSelectingCountry}
          handleCountrySelected={handleCountrySelected}
          country={country}
        />

        {/* Header */}
        <div style={{
          position: 'relative',
          background: '#f7f4ed',
          minHeight: '94px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 16px',
          borderBottomLeftRadius: '14px',
          borderBottomRightRadius: '14px',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
        >
          <button
            type="button"
            aria-label="back"
            onClick={() => history.goBack()}
            style={{
              position: 'absolute',
              left: '16px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6 1L1 6L6 11"
                stroke="#0b0b0b"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <p style={{
            fontFamily: theme.titleFont,
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: '24px',
            color: '#0b0b0b',
            margin: 0,
          }}
          >
            {i18n.t('change_profile')}
          </p>
          <GalpUserMenu initials={galpInitials} />
        </div>

        {/* Body */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" style={{ flex: 1 }}>
            <CircularProgress style={{ color: theme.loadingIconColor }} />
          </Box>
        ) : (
          <form
            id="profile"
            noValidate
            autoComplete="off"
            onSubmit={onSubmit}
            style={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              padding: '24px 16px',
              boxSizing: 'border-box',
            }}
          >
            <div style={galpFieldGroup}>
              <label htmlFor="galp-profile-name" style={galpLabel}>{i18n.t('name')}</label>
              <div style={galpInputBox}>
                <input
                  id="galp-profile-name"
                  type="text"
                  value={name || ''}
                  onChange={(e) => setName(e.target.value)}
                  style={galpInput}
                />
              </div>
            </div>

            <div style={galpFieldGroup}>
              <label htmlFor="galp-profile-email" style={galpLabel}>{i18n.t('email')}</label>
              <div style={galpInputBox}>
                <input
                  id="galp-profile-email"
                  type="email"
                  value={email || ''}
                  disabled
                  style={{ ...galpInput, color: '#888887', cursor: 'not-allowed' }}
                />
              </div>
              {error.email && <p style={galpErrorStyle}>{error.email}</p>}
            </div>

            <div style={galpFieldGroup}>
              <label htmlFor="galp-profile-nif" style={galpLabel}>
                {i18n.t('vat_number')}
                {' (NIF)'}
              </label>
              <div style={galpInputBox}>
                <button
                  type="button"
                  aria-label="select country"
                  onClick={() => setSelectingCountry(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {country === 'PT' ? (
                    <img
                      src="/companies/GALP/flag_pt.svg"
                      alt="PT"
                      style={{ width: '28px', height: '20px', display: 'block' }}
                    />
                  ) : (
                    CountryFlag && (
                      <span style={{
                        width: '28px',
                        height: '20px',
                        borderRadius: '3px',
                        overflow: 'hidden',
                        display: 'block',
                      }}
                      >
                        <CountryFlag style={{ width: '28px', height: '20px', display: 'block' }} />
                      </span>
                    )
                  )}
                </button>
                <input
                  id="galp-profile-nif"
                  type="text"
                  value={taxId.number || ''}
                  onChange={(e) => { const number = e.target.value; setTaxId((prev) => ({ ...prev, number })); }}
                  style={galpInput}
                />
              </div>
              {error.taxId && <p style={galpErrorStyle}>{error.taxId}</p>}
            </div>

            <div style={galpFieldGroup}>
              <label htmlFor="galp-profile-phone" style={galpLabel}>{i18n.t('mobile_phone_mbway')}</label>
              <div style={galpInputBox}>
                <input
                  id="galp-profile-phone"
                  type="tel"
                  value={mobilePhone || ''}
                  onChange={(e) => setMobilePhone(e.target.value)}
                  style={galpInput}
                />
              </div>
            </div>
          </form>
        )}

        {/* Footer */}
        <div style={{
          borderTop: '1px solid #bababa',
          background: 'white',
          height: '85px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 32px',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
        >
          <button
            type="submit"
            form="profile"
            disabled={submitDisabled}
            style={{
              height: '36px',
              width: '100%',
              borderRadius: '57px',
              background: submitDisabled ? '#df9680' : theme.buttonColor,
              border: 'none',
              color: theme.buttonTextColor || 'white',
              fontFamily: theme.bodyFont,
              fontSize: '14px',
              cursor: submitDisabled ? 'not-allowed' : 'pointer',
              boxShadow: '0px 2px 0px rgba(0,0,0,0.04)',
            }}
          >
            {i18n.t('save')}
          </button>
        </div>
      </div>
    );
  }

  if (isHelexia) {
    const helexiaErrorStyle: React.CSSProperties = {
      color: '#d01818',
      fontFamily: 'Geologica, sans-serif',
      fontWeight: 500,
      fontSize: '12px',
      margin: '4px 0 0 0',
    };

    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress style={{ color: theme.loadingIconColor }} />
        </Box>
      );
    }

    const isDisabled = !changed || !!error.email || !!error.taxId;

    return (
      <>
        <NotificationToast
          open={showNotification}
          message={notification && notification.message}
          title={notification && notification.title}
          severity={notification && notification.severity}
          onClose={() => setShowNotification(false)}
        />
        <TaxCountryModal
          isSelectingCountry={isSelectingCountry}
          setSelectingCountry={setSelectingCountry}
          handleCountrySelected={handleCountrySelected}
          country={country}
        />
        <div style={{
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '100%', flex: 1,
        }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '40px', width: '100%' }}>
            <p style={{
              fontFamily: 'Geologica, sans-serif',
              fontWeight: 600,
              fontSize: '32px',
              lineHeight: '32px',
              color: '#004e74',
              textAlign: 'center',
              margin: 0,
              width: '100%',
              whiteSpace: 'pre-line',
            }}
            >
              {i18n.t('profile_change_title')}
            </p>
            <form
              id="helexia-profile"
              onSubmit={onSubmit}
              style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}>
                <div>
                  <HelexiaInputWrapper>
                    <HelexiaInput
                      type="text"
                      placeholder={i18n.t('name') as string}
                      value={name || ''}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </HelexiaInputWrapper>
                </div>
                <div>
                  <HelexiaInputWrapper>
                    <HelexiaInput
                      type="email"
                      placeholder="Email"
                      value={email || ''}
                      disabled
                    />
                  </HelexiaInputWrapper>
                  {error.email && <p style={helexiaErrorStyle}>{error.email}</p>}
                </div>
                <div>
                  <div style={{
                    background: '#efefef',
                    borderRadius: '12px',
                    height: '36px',
                    padding: '0 10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectingCountry(true)}
                      style={{
                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                        flexShrink: 0, width: '25px', height: '25px', borderRadius: '50%', overflow: 'hidden', position: 'relative',
                      }}
                    >
                      {CountryFlag && (
                        <CountryFlag style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '40px', height: '40px' }} />
                      )}
                    </button>
                    <HelexiaInput
                      type="text"
                      placeholder={i18n.t('vat_number') as string}
                      value={taxId.number || ''}
                      onChange={(e) => { const number = e.target.value; setTaxId((prev) => ({ ...prev, number })); }}
                    />
                  </div>
                  {error.taxId && <p style={helexiaErrorStyle}>{error.taxId}</p>}
                </div>
              </div>
            </form>
          </div>
          <button
            type="submit"
            form="helexia-profile"
            disabled={isDisabled}
            style={{
              background: isDisabled
                ? '#d0d0d0'
                : 'linear-gradient(90deg, #fbd802 0%, #f7c749 100%)',
              borderRadius: '12px',
              height: '36px',
              border: 'none',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              color: 'white',
              fontFamily: 'Geologica, sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              letterSpacing: '-0.42px',
              width: '100%',
              flexShrink: 0,
            }}
          >
            {i18n.t('alter')}
          </button>
        </div>
      </>
    );
  }

  return loading ? (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      height="50%"
    >
      <CircularProgress style={{ color: theme.loadingIconColor }} />
    </Box>
  ) : (
    <FormContainer
      style={{
        margin: '0 2dvh 2dvh 2dvh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'start',
        height: '100%',
      }}
    >
      <NotificationToast
        open={showNotification}
        message={notification && notification.message}
        title={notification && notification.title}
        severity={notification && notification.severity}
        onClose={() => setShowNotification(false)}
      />
      <form style={{ width: '100%' }} noValidate autoComplete="off" onSubmit={onSubmit} id="profile">
        <StyledDiv>
          <span>{i18n.t('name')}</span>
          <StyledTextField
            fullWidth
            onChange={(e) => setName(e.target.value)}
            id="name"
            value={name}
          />
        </StyledDiv>
        <StyledDiv>
          <span>{i18n.t('Email')}</span>
          <StyledTextField
            fullWidth
            onChange={(e) => setEmail(e.target.value)}
            error={!!error?.email}
            id="email"
            disabled
            value={email}
            helperText={error.email ? error.email : ''}
          />
        </StyledDiv>
        <TaxCountryModal
          isSelectingCountry={isSelectingCountry}
          setSelectingCountry={setSelectingCountry}
          handleCountrySelected={handleCountrySelected}
          country={country}
        />
        <StyledDiv>
          <span>{i18n.t('vat_number')}</span>
          <StyledTextField
            fullWidth
            onChange={(e) => { const number = e.target.value; setTaxId((prev) => ({ ...prev, number })); }}
            error={!!error?.taxId}
            id="taxId"
            value={taxId.number}
            helperText={<StyledSpan>{error.taxId ? error.taxId : ''}</StyledSpan>}
            style={{
              margin: '5px 0px',
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment
                  style={{
                    width: '50px',
                    paddingBottom: '5px',
                  }}
                  position="start"
                  onClick={() => setSelectingCountry(true)}
                >
                  <CountryFlag
                    style={{
                      borderRadius: '5px',
                      width: '100%',
                    }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </StyledDiv>
        <StyledDiv>
          <span>{i18n.t('mobile_phone')}</span>
          <StyledTextField
            fullWidth
            onChange={(e) => setMobilePhone(e.target.value)}
            id="mobilePhone"
            value={mobilePhone}
          />
        </StyledDiv>
        <ButtonsContainer>
          <StyledButton
            variant="outlined"
            color={theme.buttonColor}
            textColor={theme.buttonTextColor || 'white'}
            type="submit"
            form="profile"
            disabled={!changed || !!error.email || !!error.taxId}
          >
            {i18n.t('save')}
          </StyledButton>
        </ButtonsContainer>
      </form>
      <Spacer />
    </FormContainer>
  );
};

export default withRouter(ProfileForm);
